import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  CreditCard,
  Settings as SettingsIcon,
  CheckCircle,
  XCircle,
  ExternalLink,
  Eye,
  EyeOff,
  TestTube,
  User,
  Building,
  Phone,
  Mail,
  Calendar,
  Shield,
  Copy,
  KeyRound
} from 'lucide-react'
import { MercadoPagoSettings, MercadoPagoSettingsRequest, MercadoPagoStats } from '../types/mercadopago.types'
import { mercadopagoService } from '../services/mercadopagoService'
import { adminService, AdminData, UpdateAdminData, ChangePasswordData } from '../services/adminService'
import { externalApiSettingsService } from '../services/externalApiSettingsService'
import { ExternalApiCredential, ExternalApiPermission, ExternalApiResourceDefinition } from '../types/externalApi.types'
import { ConfirmActionModal } from '../components/common/ConfirmActionModal'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

const Settings: React.FC = () => {
  const navigate = useNavigate()

  // Estados principais
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [activeTab, setActiveTab] = useState<'mercadopago' | 'api_external' | 'general'>('mercadopago')
  
  // Estados do MercadoPago
  const [settings, setSettings] = useState<MercadoPagoSettings | null>(null)
  const [formData, setFormData] = useState<MercadoPagoSettingsRequest>({
    access_token: '',
    public_key: '',
    webhook_secret: '',
    environment: 'sandbox',
    is_active: false
  })
  const [showAccessToken, setShowAccessToken] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [stats, setStats] = useState<MercadoPagoStats | null>(null)
  
  // Estados do Admin
  const [adminData, setAdminData] = useState<AdminData | null>(null)
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [profileFormData, setProfileFormData] = useState<UpdateAdminData>({
    name: '',
    company_name: '',
    phone: ''
  })
  const [passwordFormData, setPasswordFormData] = useState<ChangePasswordData>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // Estados da API Externa
  const [externalCredential, setExternalCredential] = useState<ExternalApiCredential | null>(null)
  const [externalResources, setExternalResources] = useState<ExternalApiResourceDefinition[]>([])
  const [externalPermissions, setExternalPermissions] = useState<ExternalApiPermission[]>([])
  const [externalCredentialName, setExternalCredentialName] = useState('Integração Externa')
  const [exposedClientSecret, setExposedClientSecret] = useState('')
  const [externalConfirmAction, setExternalConfirmAction] = useState<'rotate_secret' | 'disable_credential' | null>(null)

  // Carregar configurações iniciais
  useEffect(() => {
    // Aguardar um pouco para o servidor estar pronto
    const timer = setTimeout(() => {
      loadSettings()
      loadStats()
      loadAdminData()
      loadExternalApiSettings()
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [])

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const data = await mercadopagoService.getSettings()
      setSettings(data)
      
      // Preencher formulário com dados existentes
      setFormData({
        access_token: '', // Nunca mostrar o token real
        public_key: data.public_key,
        webhook_secret: '', // Nunca mostrar o secret real
        environment: data.environment,
        is_active: data.is_active
      })
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await mercadopagoService.getPaymentStats()
      setStats(data)
    } catch (err) {
      // Stats são opcionais, não mostrar erro
      logger.warn('Erro ao carregar estatísticas:', err)
    }
  }

  const loadAdminData = async () => {
    try {
      const data = await adminService.getCurrentAdmin()
      setAdminData(data)
      
      // Preencher formulário com dados existentes
      setProfileFormData({
        name: data.name || '',
        company_name: data.company_name || '',
        phone: data.phone || ''
      })
    } catch (err: any) {
      logger.error('Erro ao carregar dados do admin:', err)
      toast.error(err.message || 'Erro ao carregar dados do perfil')
    }
  }

  const loadExternalApiSettings = async () => {
    try {
      const [credential, resources, permissions] = await Promise.all([
        externalApiSettingsService.getCredentials(),
        externalApiSettingsService.getResourcesCatalog(),
        externalApiSettingsService.getPermissions()
      ])

      setExternalCredential(credential)
      setExternalResources(resources)
      setExternalPermissions(permissions)
      if (credential?.name) {
        setExternalCredentialName(credential.name)
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar configurações da API Externa')
    }
  }

  const handleGenerateExternalCredentials = async () => {
    try {
      setIsSaving(true)
      const result = await externalApiSettingsService.createCredentials(externalCredentialName)
      setExternalCredential(result.credential)
      setExposedClientSecret(result.client_secret)
      toast.success('Credenciais geradas com sucesso')
      await loadExternalApiSettings()
    } catch (err: any) {
      toast.error(err.message || 'Erro ao gerar credenciais')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRotateExternalSecret = async () => {
    try {
      setIsSaving(true)
      const result = await externalApiSettingsService.rotateSecret()
      setExternalCredential(result.credential)
      setExposedClientSecret(result.client_secret)
      toast.success('Client Secret rotacionado com sucesso')
      await loadExternalApiSettings()
    } catch (err: any) {
      toast.error(err.message || 'Erro ao rotacionar secret')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleExternalCredentialStatus = async (isActive: boolean) => {
    try {
      setIsSaving(true)
      const credential = await externalApiSettingsService.setCredentialStatus(isActive)
      setExternalCredential(credential)
      toast.success(`Credencial ${isActive ? 'ativada' : 'desativada'} com sucesso`)
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar status da credencial')
    } finally {
      setIsSaving(false)
    }
  }

  const hasPermissionSelected = (routePattern: string, method: string): boolean => {
    return externalPermissions.some(
      permission =>
        permission.route_pattern === routePattern &&
        permission.method === method &&
        permission.is_active
    )
  }

  const handlePermissionToggle = (resource: ExternalApiResourceDefinition, method: 'GET' | 'POST' | 'PUT' | 'DELETE') => {
    const exists = hasPermissionSelected(resource.route_pattern, method)

    if (exists) {
      setExternalPermissions(prev =>
        prev.filter(
          permission => !(permission.route_pattern === resource.route_pattern && permission.method === method)
        )
      )
      return
    }

    setExternalPermissions(prev => [
      ...prev,
      {
        resource_key: resource.resource_key,
        route_pattern: resource.route_pattern,
        method,
        is_active: true
      }
    ])
  }

  const handleSaveExternalPermissions = async () => {
    try {
      setIsSaving(true)
      const saved = await externalApiSettingsService.updatePermissions(externalPermissions)
      setExternalPermissions(saved)
      toast.success('Permissões atualizadas com sucesso')
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar permissões')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopyToClipboard = async (label: string, value?: string) => {
    if (!value) {
      toast.error(`${label} não disponível`)
      return
    }
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copiado com sucesso`)
    } catch {
      toast.error(`Não foi possível copiar ${label}`)
    }
  }

  const handleConfirmExternalAction = async () => {
    if (!externalConfirmAction) return

    if (externalConfirmAction === 'rotate_secret') {
      await handleRotateExternalSecret()
      setExternalConfirmAction(null)
      return
    }

    if (externalConfirmAction === 'disable_credential') {
      await handleToggleExternalCredentialStatus(false)
      setExternalConfirmAction(null)
    }
  }

  const handleInputChange = (field: keyof MercadoPagoSettingsRequest, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)

      // Validações
      if (!formData.access_token.trim()) {
        toast.error('Access Token é obrigatório')
        return
      }
      if (!formData.public_key.trim()) {
        toast.error('Public Key é obrigatório')
        return
      }

      const updatedSettings = await mercadopagoService.updateSettings(formData)
      setSettings(updatedSettings)
      toast.success('Configurações salvas com sucesso!')
      
      // Limpar campos sensíveis do formulário
      setFormData(prev => ({
        ...prev,
        access_token: '',
        webhook_secret: ''
      }))
      
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestConnection = async () => {
    try {
      setIsTesting(true)

      const result = await mercadopagoService.testConnection()
      toast.success(result.message)
      
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setIsTesting(false)
    }
  }

  const handleProfileInputChange = (field: keyof UpdateAdminData, value: string) => {
    setProfileFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePasswordInputChange = (field: keyof ChangePasswordData, value: string) => {
    setPasswordFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateProfile = async () => {
    try {
      setIsSaving(true)

      const updatedData = await adminService.updateCurrentAdmin(profileFormData)
      setAdminData(updatedData)
      setIsEditingProfile(false)
      toast.success('Perfil atualizado com sucesso!')
      
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    try {
      setIsSaving(true)

      await adminService.changePassword(passwordFormData)
      setIsChangingPassword(false)
      setPasswordFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
      toast.success('Senha alterada com sucesso!')
      
    } catch (err: any) {
      toast.error(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleBackClick = () => {
    navigate('/dashboard')
  }

  // Função utilitária para formatar preço
  const formatPrice = (price: any): string => {
    if (!price) return '0.00'
    if (typeof price === 'number') {
      return price.toFixed(2)
    }
    const parsed = parseFloat(price.toString())
    return isNaN(parsed) ? '0.00' : parsed.toFixed(2)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Carregando configurações...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackClick}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                aria-label="Voltar para o dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Configurações
                </h1>
                <p className="text-gray-600">Gerencie suas integrações e configurações</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('mercadopago')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'mercadopago'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4" />
                  <span>MercadoPago</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('general')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'general'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <SettingsIcon className="w-4 h-4" />
                  <span>Geral</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('api_external')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'api_external'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <KeyRound className="w-4 h-4" />
                  <span>API Externa</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Conteúdo das Tabs */}
        {activeTab === 'mercadopago' && (
          <div className="space-y-6">
            {/* Status da Integração */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Status da Integração</h2>
                <div className="flex items-center space-x-2">
                  {settings?.is_active ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Ativa
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <XCircle className="w-3 h-3 mr-1" />
                      Inativa
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    settings?.environment === 'production' 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {settings?.environment === 'production' ? 'Produção' : 'Sandbox'}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-500">Access Token</div>
                  <div className="text-sm text-gray-900">
                    {settings?.has_access_token ? 'Configurado' : 'Não configurado'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-500">Public Key</div>
                  <div className="text-sm text-gray-900">
                    {settings?.public_key ? 'Configurado' : 'Não configurado'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-500">Webhook Secret</div>
                  <div className="text-sm text-gray-900">
                    {settings?.has_webhook_secret ? 'Configurado' : 'Não configurado'}
                  </div>
                </div>
              </div>
            </div>

            {/* Configurações */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Configurações do MercadoPago</h2>
              
              <div className="space-y-6">
                {/* Access Token */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Token *
                  </label>
                  <div className="relative">
                    <input
                      type={showAccessToken ? 'text' : 'password'}
                      value={formData.access_token}
                      onChange={(e) => handleInputChange('access_token', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Digite seu access token do MercadoPago"
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccessToken(!showAccessToken)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showAccessToken ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Token de acesso obtido no painel do MercadoPago
                  </p>
                </div>

                {/* Public Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public Key *
                  </label>
                  <input
                    type="text"
                    value={formData.public_key}
                    onChange={(e) => handleInputChange('public_key', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Digite sua public key do MercadoPago"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Chave pública obtida no painel do MercadoPago
                  </p>
                </div>

                {/* Webhook Secret */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Webhook Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showWebhookSecret ? 'text' : 'password'}
                      value={formData.webhook_secret}
                      onChange={(e) => handleInputChange('webhook_secret', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                      placeholder="Digite o secret do webhook (opcional)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showWebhookSecret ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Secret para validação de webhooks (opcional)
                  </p>
                </div>

                {/* Environment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ambiente
                  </label>
                  <select
                    value={formData.environment}
                    onChange={(e) => handleInputChange('environment', e.target.value as 'sandbox' | 'production')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="sandbox">Sandbox (Teste)</option>
                    <option value="production">Produção</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Use Sandbox para testes e Produção para pagamentos reais
                  </p>
                </div>

                {/* Ativar Integração */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    Ativar integração com MercadoPago
                  </label>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Salvando...' : 'Salvar Configurações'}
                </button>
                
                <button
                  onClick={handleTestConnection}
                  disabled={isTesting || !settings?.has_access_token}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <TestTube className="w-4 h-4" />
                    <span>{isTesting ? 'Testando...' : 'Testar Conexão'}</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Estatísticas */}
            {stats && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Estatísticas de Pagamentos</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-600">{stats.total_payments}</div>
                    <div className="text-sm text-blue-800">Total de Pagamentos</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">{stats.successful_payments}</div>
                    <div className="text-sm text-green-800">Pagamentos Aprovados</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">{stats.pending_payments}</div>
                    <div className="text-sm text-yellow-800">Pagamentos Pendentes</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-600">{stats.failed_payments}</div>
                    <div className="text-sm text-red-800">Pagamentos Rejeitados</div>
                  </div>
                </div>
              </div>
            )}

            {/* Links Úteis */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Links Úteis</h2>
              
              <div className="space-y-3">
                <a
                  href="https://www.mercadopago.com.br/developers/panel/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Painel de Credenciais do MercadoPago</span>
                </a>
                
                <a
                  href="https://www.mercadopago.com.br/developers/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Documentação da API</span>
                </a>
                
                <a
                  href="https://www.mercadopago.com.br/developers/panel/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Configuração de Webhooks</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api_external' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Credenciais da API Externa</h2>
              <p className="text-sm text-gray-600 mb-6">
                Gere credenciais para integrar sistemas externos ao seu atendimento, com escopo isolado por administrador.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Integração</label>
                  <input
                    type="text"
                    value={externalCredentialName}
                    onChange={(e) => setExternalCredentialName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex.: Site Institucional"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Client ID</div>
                    <div className="text-sm font-mono break-all text-gray-900">
                      {externalCredential?.client_id || 'Não gerado'}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyToClipboard('Client ID', externalCredential?.client_id)}
                      className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Client Secret (somente exibição atual)</div>
                    <div className="text-sm font-mono break-all text-gray-900">
                      {exposedClientSecret || 'Disponível apenas ao gerar/rotacionar'}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCopyToClipboard('Client Secret', exposedClientSecret)}
                      className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <button
                    onClick={handleGenerateExternalCredentials}
                    disabled={isSaving}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Processando...' : 'Gerar credenciais'}
                  </button>
                  <button
                    onClick={() => setExternalConfirmAction('rotate_secret')}
                    disabled={isSaving || !externalCredential}
                    className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Rotacionar secret
                  </button>
                  <button
                    onClick={() => {
                      if (externalCredential?.is_active) {
                        setExternalConfirmAction('disable_credential')
                        return
                      }
                      handleToggleExternalCredentialStatus(true)
                    }}
                    disabled={isSaving || !externalCredential}
                    className={`px-4 py-2 rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                      externalCredential?.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {externalCredential?.is_active ? 'Desativar credencial' : 'Ativar credencial'}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Controles permitidos</h2>
              <p className="text-sm text-gray-600 mb-6">
                Marque os métodos permitidos por rota para uso externo. Apenas recursos do catálogo oficial são aceitos.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurso</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rota</th>
                      {(['GET', 'POST', 'PUT', 'DELETE'] as const).map(method => (
                        <th key={method} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{method}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {externalResources.map(resource => (
                      <tr key={`${resource.resource_key}-${resource.route_pattern}`}>
                        <td className="px-4 py-3 text-sm text-gray-900">{resource.resource_key}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{resource.route_pattern}</td>
                        {(['GET', 'POST', 'PUT', 'DELETE'] as const).map(method => {
                          const methodAllowedForResource = resource.methods.includes(method)
                          const checked = hasPermissionSelected(resource.route_pattern, method)
                          return (
                            <td key={`${resource.route_pattern}-${method}`} className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                disabled={!methodAllowedForResource}
                                checked={checked}
                                onChange={() => handlePermissionToggle(resource, method)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-40"
                              />
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6">
                <button
                  onClick={handleSaveExternalPermissions}
                  disabled={isSaving || !externalCredential}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Salvando...' : 'Salvar permissões'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Exemplo de consumo</h2>
              <div className="space-y-4 text-sm text-gray-700">
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                  Ative em <strong>Controles permitidos</strong> os métodos necessários, incluindo{' '}
                  <span className="font-mono">GET /tags</span> para o catálogo de tags e{' '}
                  <span className="font-mono">GET /conversations</span> com os parâmetros abaixo para filtrar por tags
                  (alinhado ao painel <span className="font-mono">/conversations</span> e ao widget embutido).
                </p>
                <p><strong>Headers (todas as rotas):</strong></p>
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 overflow-x-auto text-xs">
{`x-client-id: ${externalCredential?.client_id || 'Client_Id_xxx'}
x-client-secret: ${exposedClientSecret || 'Client_Secret_xxx'}`}
                </pre>
                <p className="text-xs text-gray-500">
                  Após o login do atendente (<span className="font-mono">POST /api/external/v1/auth/login</span>), use{' '}
                  <span className="font-mono">Authorization: Bearer &lt;token&gt;</span> nas rotas abaixo.
                </p>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Catálogo de tags (visibilidade do atendente)</p>
                  <p className="font-mono text-xs break-all mb-1">GET /api/external/v1/tags</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Listagem de conversas com tags na resposta e filtro opcional</p>
                  <p className="font-mono text-xs break-all mb-1">
                    GET /api/external/v1/conversations?page=1&amp;limit=20&amp;include_finished=false&amp;include_tags=true
                  </p>
                  <p className="font-mono text-xs break-all text-gray-600">
                    &amp;tag_ids=1,2&amp;tag_match=any
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="font-mono">tag_ids</span>: IDs separados por vírgula (tags visíveis ao atendente).{' '}
                    <span className="font-mono">tag_match</span>: <span className="font-mono">any</span> (predefinição) ou{' '}
                    <span className="font-mono">all</span>. Omitir <span className="font-mono">tag_ids</span> para não filtrar.
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  O widget oficial carrega o catálogo, aplica o mesmo critério de «tags mais usadas» na lista atual e envia estes parâmetros ao atualizar conversas.
                </p>
                <p className="text-xs text-gray-500">
                  Observação: o Client Secret completo é exibido apenas no momento da geração ou rotação. Armazene em local seguro.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'general' && (
          <div className="space-y-6">
            {/* Dados do Admin */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Dados Pessoais</h2>
                {!isEditingProfile && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Editar
                  </button>
                )}
              </div>

              {!isEditingProfile ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Nome Completo</div>
                        <div className="text-sm text-gray-900">{adminData?.name || 'Não informado'}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Email</div>
                        <div className="text-sm text-gray-900">{adminData?.email || 'Não informado'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Building className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Empresa</div>
                        <div className="text-sm text-gray-900">{adminData?.company_name || 'Não informado'}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Telefone</div>
                        <div className="text-sm text-gray-900">{adminData?.phone || 'Não informado'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      value={profileFormData.name}
                      onChange={(e) => handleProfileInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={adminData?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                      placeholder="Email não pode ser alterado"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      O email não pode ser alterado por questões de segurança
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Empresa
                    </label>
                    <input
                      type="text"
                      value={profileFormData.company_name}
                      onChange={(e) => handleProfileInputChange('company_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Digite o nome da empresa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={profileFormData.phone}
                      onChange={(e) => handleProfileInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="5567991644021"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Formato: 5567991644021 (código do país + DDD + número)
                    </p>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleUpdateProfile}
                      disabled={isSaving}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingProfile(false)
                        setProfileFormData({
                          name: adminData?.name || '',
                          company_name: adminData?.company_name || '',
                          phone: adminData?.phone || ''
                        })
                      }}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Alterar Senha */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Segurança</h2>
                {!isChangingPassword && (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Alterar Senha
                  </button>
                )}
              </div>

              {isChangingPassword && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Senha Atual *
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordFormData.current_password}
                        onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="Digite sua senha atual"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nova Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordFormData.new_password}
                        onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="Digite sua nova senha"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Mínimo de 6 caracteres
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Nova Senha *
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordFormData.confirm_password}
                        onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="Confirme sua nova senha"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={handleChangePassword}
                      disabled={isSaving}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Alterando...' : 'Alterar Senha'}
                    </button>
                    <button
                      onClick={() => {
                        setIsChangingPassword(false)
                        setPasswordFormData({
                          current_password: '',
                          new_password: '',
                          confirm_password: ''
                        })
                      }}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Dados da Assinatura */}
            {adminData?.subscriptions && adminData.subscriptions.length > 0 && adminData.subscriptions[0] && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Plano Ativo</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Plano</div>
                        <div className="text-sm text-gray-900">
                          {adminData.subscriptions[0]?.plan?.name || 'Não informado'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">Expira em</div>
                        <div className="text-sm text-gray-900">
                          {adminData.subscriptions[0]?.expiration_date 
                            ? new Date(adminData.subscriptions[0].expiration_date).toLocaleDateString('pt-BR')
                            : 'Não informado'
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <div className={`w-3 h-3 rounded-full ${
                          adminData.subscriptions[0]?.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500">Status</div>
                        <div className={`text-sm font-medium ${
                          adminData.subscriptions[0]?.status === 'active' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {adminData.subscriptions[0]?.status === 'active' ? 'Ativo' : 'Inativo'}
                        </div>
                      </div>
                    </div>
                    
                    {adminData.subscriptions[0]?.plan?.price && (
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 text-gray-400">R$</div>
                        <div>
                          <div className="text-sm font-medium text-gray-500">Valor</div>
                          <div className="text-sm text-gray-900">
                            R$ {formatPrice(adminData.subscriptions[0]?.plan?.price)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {externalConfirmAction && (
        <ConfirmActionModal
          isOpen
          onClose={() => !isSaving && setExternalConfirmAction(null)}
          onConfirm={handleConfirmExternalAction}
          title={externalConfirmAction === 'rotate_secret' ? 'Rotacionar Client Secret' : 'Desativar credencial'}
          message={
            externalConfirmAction === 'rotate_secret' ? (
              <>
                Ao rotacionar o <strong>Client Secret</strong>, integrações externas que usam o segredo atual deixarão de funcionar
                imediatamente. Deseja continuar?
              </>
            ) : (
              <>
                Ao desativar a credencial, todas as chamadas externas com este <strong>Client ID</strong> serão bloqueadas
                até que você ative novamente. Deseja continuar?
              </>
            )
          }
          confirmLabel={externalConfirmAction === 'rotate_secret' ? 'Rotacionar' : 'Desativar'}
          variant={externalConfirmAction === 'rotate_secret' ? 'warning' : 'danger'}
          icon={externalConfirmAction === 'rotate_secret' ? KeyRound : XCircle}
          isLoading={isSaving}
        />
      )}
    </div>
  )
}

export default Settings



